import { useEffect, useMemo, useRef, useState } from "react";
import {
  addDoc,
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../../lib/firebase";

let chatAudioCtx = null;

function playChatChime() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;

    if (!chatAudioCtx) {
      chatAudioCtx = new AudioCtx();
    }

    if (chatAudioCtx.state === "suspended") {
      chatAudioCtx.resume();
    }

    const now = chatAudioCtx.currentTime;

    const gain = chatAudioCtx.createGain();
    gain.gain.setValueAtTime(0.06, now);
    gain.connect(chatAudioCtx.destination);

    const osc = chatAudioCtx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(1046, now);
    osc.connect(gain);
    osc.start(now);
    osc.stop(now + 0.16);
  } catch {
    // ignore
  }
}

function tsMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  return 0;
}

function formatTime(value) {
  const ms = tsMillis(value);
  if (!ms) return "";
  return new Date(ms).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function DispatchChat({
  open,
  setOpen,
  unreadCount,
  setUnreadCount,
  setHasUnreadChat,
}) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [dispatcherName, setDispatcherName] = useState(
    localStorage.getItem("dispatchChatName") || ""
  );
  const [nameDraft, setNameDraft] = useState(
    localStorage.getItem("dispatchChatName") || ""
  );
  const [sending, setSending] = useState(false);

  const bottomRef = useRef(null);
  const previousLatestIdRef = useRef("");
  const initializedRef = useRef(false);

  const messagesRef = useMemo(
    () => collection(db, "dispatchChat", "main", "messages"),
    []
  );

  useEffect(() => {
    const q = query(messagesRef, orderBy("createdAt", "asc"), limit(100));

    const unsub = onSnapshot(q, (snap) => {
      const nextMessages = snap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const latest = nextMessages[nextMessages.length - 1];
      const latestId = latest?.id || "";

      if (!initializedRef.current) {
        initializedRef.current = true;
        previousLatestIdRef.current = latestId;
        setMessages(nextMessages);
        return;
      }

      const isNewMessage =
        latestId &&
        latestId !== previousLatestIdRef.current &&
        latest?.sender !== dispatcherName;

      previousLatestIdRef.current = latestId;
      setMessages(nextMessages);

      if (isNewMessage) {
  playChatChime();

  if (!open) {
    setHasUnreadChat(true);
    setUnreadCount((prev) => prev + 1);
  }
}
    });

    return () => unsub();
  }, [messagesRef, dispatcherName, open, setHasUnreadChat, setUnreadCount]);

  useEffect(() => {
    if (!open) return;

    setHasUnreadChat(false);
    setUnreadCount(0);

    setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 50);
  }, [open, messages, setHasUnreadChat, setUnreadCount]);

  function saveName() {
    const clean = nameDraft.trim();
    if (!clean) return;

    localStorage.setItem("dispatchChatName", clean);
    setDispatcherName(clean);
  }

  async function sendMessage(e) {
    e?.preventDefault?.();

    const cleanText = text.trim();
    const cleanName = dispatcherName.trim();

    if (!cleanName) {
      alert("Please enter your name first.");
      return;
    }

    if (!cleanText) return;

    setSending(true);

    try {
      await addDoc(messagesRef, {
        text: cleanText,
        sender: cleanName,
        createdAt: serverTimestamp(),
      });

      setText("");
    } catch (err) {
      console.error(err);
      alert("Could not send chat message.");
    }

    setSending(false);
  }

  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        right: 14,
        bottom: 14,
        width: 360,
        maxWidth: "calc(100vw - 28px)",
        height: 440,
        maxHeight: "calc(100dvh - 80px)",
        background: "#0f172a",
        border: "1px solid #334155",
        borderRadius: 12,
        boxShadow: "0 20px 50px rgba(0,0,0,0.45)",
        zIndex: 9999,
        color: "#e2e8f0",
        display: "grid",
        gridTemplateRows: "auto auto minmax(0, 1fr) auto",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "9px 10px",
          background: "linear-gradient(180deg, #172554 0%, #0f172a 100%)",
          borderBottom: "1px solid #22314d",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 900 }}>Dispatch Chat</div>

        <button
          type="button"
          onClick={() => setOpen(false)}
          style={smallButton}
        >
          Close
        </button>
      </div>

      <div
        style={{
          padding: 8,
          borderBottom: "1px solid #22314d",
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 6,
          background: "#10192c",
        }}
      >
        <input
          value={nameDraft}
          onChange={(e) => setNameDraft(e.target.value)}
          placeholder="Dispatcher name"
          style={inputStyle}
        />

        <button type="button" onClick={saveName} style={smallButton}>
          Save Name
        </button>

        {!!dispatcherName && (
          <div
            style={{
              gridColumn: "1 / -1",
              fontSize: 10,
              color: "#94a3b8",
            }}
          >
            Sending as: <b style={{ color: "#e2e8f0" }}>{dispatcherName}</b>
          </div>
        )}
      </div>

      <div
        style={{
          minHeight: 0,
          overflowY: "auto",
          padding: 8,
          display: "grid",
          gap: 6,
          alignContent: "start",
          background: "#08111f",
        }}
      >
        {!messages.length && (
          <div style={{ fontSize: 11, color: "#94a3b8" }}>
            No chat messages yet.
          </div>
        )}

        {messages.map((msg) => {
          const mine = msg.sender === dispatcherName;

          return (
            <div
              key={msg.id}
              style={{
                justifySelf: mine ? "end" : "start",
                maxWidth: "85%",
                background: mine ? "#102844" : "#172033",
                border: mine ? "1px solid #2563eb" : "1px solid #334155",
                color: "#e2e8f0",
                borderRadius: 10,
                padding: "6px 8px",
              }}
            >
              <div
                style={{
                  fontSize: 9,
                  color: mine ? "#bfdbfe" : "#94a3b8",
                  fontWeight: 900,
                  marginBottom: 3,
                }}
              >
                {msg.sender || "Dispatcher"}{" "}
                <span style={{ fontWeight: 700, opacity: 0.75 }}>
                  {formatTime(msg.createdAt)}
                </span>
              </div>

              <div style={{ fontSize: 11, lineHeight: 1.25, whiteSpace: "pre-wrap" }}>
                {msg.text}
              </div>
            </div>
          );
        })}

        <div ref={bottomRef} />
      </div>

      <form
        onSubmit={sendMessage}
        style={{
          padding: 8,
          borderTop: "1px solid #22314d",
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 6,
          background: "#10192c",
        }}
      >
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type message..."
          style={inputStyle}
        />

        <button
          type="submit"
          disabled={sending || !text.trim()}
          style={{
            ...smallButton,
            opacity: sending || !text.trim() ? 0.55 : 1,
          }}
        >
          Send
        </button>
      </form>
    </div>
  );
}

const inputStyle = {
  width: "100%",
  borderRadius: 8,
  border: "1px solid #334155",
  background: "#0b1220",
  color: "#e2e8f0",
  minHeight: 30,
  padding: "0 8px",
  outline: "none",
  fontSize: 11,
  boxSizing: "border-box",
};

const smallButton = {
  border: "1px solid #334155",
  borderRadius: 8,
  padding: "5px 9px",
  color: "#e2e8f0",
  background: "#111c2f",
  fontSize: 10,
  fontWeight: 900,
  cursor: "pointer",
  whiteSpace: "nowrap",
};